/**
 * 后端 multipart/complete-upload 会先 registerStoredUpload（id=存储文件名），
 * 随后前端再以客户端临时 id（如 LIB-xxx）调用 image-space/batch，
 * 服务端按 url/hash 去重后回包 id 可能与请求不一致。按 id / url 对齐，避免列表丢 url。
 */

export function normalizeImageSpaceCorrelateUrl(url) {
  const s = String(url || '').trim().replace(/\\/g, '/');
  if (!s || s.startsWith('data:') || s.startsWith('blob:') || s.startsWith('indexeddb://')) {
    return '';
  }
  return s.split('?')[0];
}

function stripImageNameExtension(name) {
  const raw = String(name || '').trim();
  if (!raw) return '';
  const base = raw.includes('/') ? raw.split('/').pop() : raw;
  const dot = base.lastIndexOf('.');
  if (dot > 0 && dot < base.length - 1) return base.slice(0, dot);
  return base;
}

/** OSS 叶子名 / UUID：不能当作用户可见文件名 */
export function isOpaqueImageSpaceDisplayName(name, recordId = '') {
  const base = stripImageNameExtension(name);
  if (!base) return true;
  const recordBase = stripImageNameExtension(recordId);
  if (recordBase && base.toLowerCase() === recordBase.toLowerCase()) return true;
  if (/^img-[a-f0-9]{16,}$/i.test(base)) return true;
  if (/^[a-f0-9]{32}$/i.test(base)) return true;
  if (/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(base)) {
    return true;
  }
  return false;
}

function pickCorrelatedDisplayName(localName, matchedName, recordId) {
  const local = String(localName || '').trim();
  const matched = String(matchedName || '').trim();
  let picked = '';
  if (local && !isOpaqueImageSpaceDisplayName(local, recordId)) picked = local;
  else if (matched && !isOpaqueImageSpaceDisplayName(matched, recordId)) picked = matched;
  else picked = local || matched || '';
  // 展示名不带后缀：18.png → 18
  return stripImageNameExtension(picked) || picked;
}

/**
 * @param {Array<object>} requestItems 入库请求项（含客户端 id + 已落盘 url）
 * @param {Array<object>} savedImages 服务端回包 images（id 可能已是存储键）
 * @returns {Array<object>} 与 request 一一对应；附带 clientLocalId
 */
export function correlateImageSpaceBatchSaveResults(requestItems, savedImages) {
  const request = Array.isArray(requestItems) ? requestItems : [];
  const saved = Array.isArray(savedImages) ? savedImages : [];
  if (request.length === 0) return saved;

  const savedById = new Map();
  const savedByUrl = new Map();
  for (const item of saved) {
    if (!item || item.id == null || item.id === '') continue;
    savedById.set(String(item.id), item);
    const u = normalizeImageSpaceCorrelateUrl(item.url);
    if (u && !savedByUrl.has(u)) savedByUrl.set(u, item);
  }

  return request.map((local) => {
    if (!local || typeof local !== 'object') return local;
    const localId = local.id != null && local.id !== '' ? String(local.id) : '';
    const localUrl = normalizeImageSpaceCorrelateUrl(local.url);

    let matched = localId ? savedById.get(localId) : null;
    if (!matched && localUrl) {
      matched = savedByUrl.get(localUrl) || null;
    }

    if (!matched) {
      return {
        ...local,
        ...(localId ? { clientLocalId: localId } : {}),
      };
    }

    const serverId = matched.id != null && matched.id !== '' ? String(matched.id) : localId;
    const clientLocalId = localId && localId !== serverId
      ? localId
      : (matched.clientLocalId != null ? String(matched.clientLocalId) : localId || undefined);

    return {
      ...local,
      ...matched,
      id: serverId || localId,
      ...(clientLocalId ? { clientLocalId } : {}),
      url: matched.url || local.url || '',
      viewUrl: matched.viewUrl || matched.storedViewUrl || local.viewUrl || '',
      displayUrl:
        matched.displayUrl
        || matched.viewUrl
        || matched.storedUrlDisplay
        || local.displayUrl
        || '',
      name: pickCorrelatedDisplayName(local.name, matched.name, serverId || localId),
    };
  });
}
